import { ArgosClient } from '@jusbrasil-web/shared-argos-client'
import { isAllowedApiOrigin } from '@jusbrasil-web/shared-auth-flows'
import { NextApiHandler } from 'next'

import {
  buildEntityNoticePersistMetadata,
  isEntityNoticePageKind,
  isEntityNoticeVariant,
} from '../experiments/entity-notice-fakedoor'

const ARGOS_EVENT_NAME = 'Topic.EntityNoticeFakedoor'

export interface EntityNoticePersistResponse {
  recorded: boolean
}

type RejectReason = 'origin' | 'parse' | 'label' | 'emitter'

// Tags each fail-open recorded:false so this stays debuggable in production —
// a bad origin reads apart from an emitter outage. No PII (reason + origin host).
function logRejected(reason: RejectReason, origin?: string): void {
  console.warn('[auth] entityNoticePersistHandler rejected', { reason, origin })
}

/**
 * Server-side emitter for cells A/B/C's `Topic.EntityNoticeFakedoor`
 * `action:'persist'` (QA2-8c). Lives in `apps/auth` because the fake-door
 * signup document is served from there — no `auth-server` call, no
 * `apps/lawsuit` BFF route. `device_id` is read from the `jdid` cookie
 * here, the single place that resolves it; the client never supplies one.
 * Fail-open like the sibling `signup-modal-metrics-bridge-handler`: a
 * fire-and-forget caller can't act on a 4xx, so every rejection still
 * answers 200 with `recorded:false`, and an emitter failure never
 * surfaces past this handler either.
 */
export const entityNoticePersistHandler: NextApiHandler<EntityNoticePersistResponse> = async (
  req,
  res
) => {
  if (req.method !== 'POST') {
    res.setHeader('Allow', 'POST')
    res.status(405).end()
    return
  }

  res.setHeader('Cache-Control', 'no-store')

  if (!isAllowedApiOrigin(req.headers.origin)) {
    logRejected('origin', req.headers.origin)
    res.status(200).json({ recorded: false })
    return
  }

  const body = req.body
  if (body == null || typeof body !== 'object') {
    logRejected('parse', req.headers.origin)
    res.status(200).json({ recorded: false })
    return
  }

  const { email, entityId, variant, pageKind, source } = body as Record<string, unknown>

  if (
    typeof email !== 'string' ||
    !email ||
    typeof entityId !== 'string' ||
    !entityId ||
    !isEntityNoticeVariant(variant)
  ) {
    logRejected('label', req.headers.origin)
    res.status(200).json({ recorded: false })
    return
  }

  const resolvedPageKind = isEntityNoticePageKind(pageKind) ? pageKind : null
  const resolvedSource = typeof source === 'string' && source ? source : 'unknown'
  const deviceId = req.cookies?.jdid ?? ''

  const metadata = await buildEntityNoticePersistMetadata({
    email,
    entityId,
    variant,
    pageKind: resolvedPageKind,
    deviceId,
    source: resolvedSource,
  })

  try {
    new ArgosClient().send(undefined, ARGOS_EVENT_NAME, { metadata })
  } catch (err) {
    logRejected('emitter', req.headers.origin)
    // eslint-disable-next-line no-console
    console.error('[auth] entityNoticePersistHandler failed to emit', err)
  }

  res.status(200).json({ recorded: true })
}

export const entityNoticePersistApiConfig = {
  api: {
    bodyParser: { sizeLimit: '4kb' },
  },
}
